// pages/core-info-page/CoreInfoPage.tsx
import { Form, Row, Col, Spin, Card, Alert, App } from 'antd';
import type { ButtonProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import CoreInfoGeneralInfoSection from '@components/core-info/general-info-section/CoreInfoGeneralInfoSectionGraphQl';
import CoreInfoMeterSourceInfoSection from '@components/core-info/meter-source-info-section/CoreInfoMeterSourceInfoSectionGraphQl';
import CoreInfoTariffContractInfoSection from '@components/core-info/tariff-contract-info-section/CoreInfoTariffContractInfoSectionGraphQl';
import CoreInfoReplacementMeterInfoSection from '@components/core-info/replacement-meter-info-section/CoreInfoReplacementMeterInfoSectionGraphQl';
import { useCoreInfoPage } from './useCoreInfoPage';
import { NSPButton } from '@components/ui/NSPButton';
import PageLayout from '@components/layout/PageLayout';
import styles from './CoreInfoPage.module.css';

function CoreInfoPageInner() {
  const {
    loading, updating, id, form, canEdit, data, onFinish,
    fetchError, isNewMeter, updateError, saveSuccess, setSaveSuccess,
    ippLookupLoading, handleVendorBlur,
  } = useCoreInfoPage();

  const { modal } = App.useApp();
  const navigate = useNavigate();
  const AntCard = Card as unknown as React.ComponentType<any>;

  if (!id) {
    return <div className={styles.missingId} data-testid="core-info-missing-id">Missing meter id.</div>;
  }

  if (loading || updating) {
    return <Spin data-testid="loading-spinner" />;
  }

  const pageTitle = `Meter Core Info${data ? ' - Meter ' + data.meterNumber + ' at ' + data.meterName : ''}`;

  return (
    <PageLayout title={pageTitle}>
      <div className={styles.content}>
        {(fetchError || updateError) && (
          <Alert
            message="Error"
            description={fetchError?.message || updateError?.message}
            type="error"
            showIcon
            closable
            style={{ marginBottom: '16px' }}
            data-testid="core-info-error-alert"
          />
        )}

        {saveSuccess && (
          <Alert
            message="Success"
            description="Meter core info saved successfully"
            type="success"
            showIcon
            closable
            onClose={() => setSaveSuccess(false)}
            style={{ marginBottom: '16px' }}
            data-testid="core-info-success-alert"
          />
        )}

        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          disabled={!canEdit}
          data-testid="core-info-form"
          data-meter-id={id}
          data-editable={String(canEdit)}
          data-new-meter={String(isNewMeter)}
          initialValues={{
            meterId: '',
            meterNumber: '',
            meterName: '',
            ipp: { ippId: '', vendorName: '', purchaseOrderNo: '', needDetailsWithInvoice: '' },
            energy: '',
            region: '',
            statGl: { glNumber: '' },
            dollarGl: { glNumber: '' },
            meterFileSource: '',
            kwhGenerated: '',
            kwhUsed: '',
            rateContracts: [],
            selectedRateId: undefined,
            retroactiveReminder: undefined,
            selectedRateType: undefined,
            energyBid: '',
            applyHst: false,
            forecastFee: undefined,
            forecastFeeCurrency: undefined,
            forecastFeeGl: { glNumber: '' },
            adminFeeRate: undefined,
            adminFeeGl: { glNumber: '' },
          }}
        >
          <Form.Item name="meterId" noStyle>
            <input type="hidden" />
          </Form.Item>
          <Form.Item name="meterNumber" noStyle>
            <input type="hidden" />
          </Form.Item>

          <Row gutter={24}>
            <Col xs={24} md={8}>
              <AntCard title="General Info" variant="contained" className={styles.cardSection} data-testid="core-info-section" data-section="general-info">
                <CoreInfoGeneralInfoSection
                  onVendorBlur={handleVendorBlur}
                  ippLookupLoading={ippLookupLoading}
                />
              </AntCard>

              <AntCard title="Meter Source Info" variant="contained" className={styles.cardSection} data-testid="core-info-section" data-section="meter-source-info">
                <CoreInfoMeterSourceInfoSection form={form} />
              </AntCard>
            </Col>

            <Col xs={24} md={16}>
              <AntCard title="Tariff / Contract Info" className={styles.cardSection} data-testid="core-info-section" data-section="tariff-contract-info">
                <CoreInfoTariffContractInfoSection form={form} disabled={!canEdit} />
              </AntCard>

              {!isNewMeter && (
                <AntCard title="Replacement Meter Info" variant="contained" className={styles.cardSection} data-testid="core-info-section" data-section="replacement-meter-info">
                  <CoreInfoReplacementMeterInfoSection form={form} disabled={!canEdit} />
                </AntCard>
              )}
            </Col>
          </Row>

          {canEdit && (
            <Row justify="end" gutter={16} className={styles.actionRow}>
              <Col>
                <NSPButton
                  data-testid="cancel-button"
                  danger
                  onClick={() => {
                    if (form.isFieldsTouched()) {
                      modal.confirm({
                        title: 'Discard changes?',
                        content: 'Are you sure you want to cancel and lose your changes?',
                        okButtonProps: { 'data-testid': 'modal-ok-button' } as ButtonProps,
                        cancelButtonProps: { 'data-testid': 'modal-cancel-button' } as ButtonProps,
                        onOk: () => navigate(-1),
                      });
                    } else {
                      navigate(-1);
                    }
                  }}
                >
                  Cancel
                </NSPButton>
              </Col>
              <Col>
                <NSPButton htmlType="submit" data-testid="save-button">Save</NSPButton>
              </Col>
            </Row>
          )}
        </Form>
      </div>
    </PageLayout>
  );
}

export default function CoreInfoPage() {
  return (
    <App>
      <CoreInfoPageInner />
    </App>
  );
}